import React, { useMemo } from "react";
import { Linking } from "react-native";
import styled from "styled-components/native";
import { Spacer, VDBText } from "@components/atoms";
import { SpaceType } from "@components/atoms/Spacer/types";
import LabelView from "@components/molecules/LabelView";
import CollapsibleView from "@components/molecules/CollapsibleView";
import LineViewContainer from "./LineView";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTheme } from "styled-components/native";
import { useTranslation } from "react-i18next";
import JBTDetail from "@components/molecules/JBTDetail";
import { useSelector } from "react-redux";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import {
  getRequestType,
  RequestTypes,
  showRequestItemFromSupplier,
} from "./utils";
import { StockItemRequestClass } from "@src/types/HistoryRequest";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import * as Clipboard from "expo-clipboard";
import { showToast, ToastTypes } from "@components/molecules/VDBToast/util";

interface CompanyDetailsCardProps {
  stockItemRequest: StockItemRequestClass;
  stockItemRequestParent: StockItemRequestClass;
  productType: {
    productType: string;
    bulkProductName: string;
    productIcon: string;
    theme: string;
  };
}

const CompanyDetailsCard = ({
  stockItemRequest,
  stockItemRequestParent,
}: CompanyDetailsCardProps) => {
  const typography = useTypographyStore();
  const theme = useTheme();
  const { t } = useTranslation();
  const isCustomApp = useSelector((state: any) => state.appState?.isCustomApp);
  const { isJbtDisabled } = useOrgSettingsSlice();
  const requestType = getRequestType(stockItemRequest.request_type);

  let keyWidthPercent = theme.isMobile ? 35 : 18;

  let recipientEmailAvailable =
    stockItemRequest?.quote_details?.is_user_registered;

  const { isPhoneNumberVisible } = useMemo(() => {
    let shareType = stockItemRequest?.additional_attributes?.share_type;
    if (stockItemRequestParent) {
      shareType = stockItemRequestParent?.additional_attributes?.share_type;
    }
    return {
      isPhoneNumberVisible:
        shareType !== "share_via_email" && requestType === RequestTypes.Quote,
    };
  }, [
    requestType,
    stockItemRequest?.additional_attributes?.share_type,
    stockItemRequestParent,
  ]);

  const { isEmailVisible } = useMemo(() => {
    let shareType = stockItemRequest?.additional_attributes?.share_type;
    if (stockItemRequestParent) {
      shareType = stockItemRequestParent?.additional_attributes?.share_type;
    }
    return {
      isEmailVisible:
        shareType?.toLowerCase() !== "share_via_whatsapp" &&
        requestType === RequestTypes.Quote,
    };
  }, [
    requestType,
    stockItemRequest?.additional_attributes?.share_type,
    stockItemRequestParent,
  ]);

  const originalSellerLocation = useMemo(() => {
    return stockItemRequest?.original_seller_location
      ? stockItemRequest.original_seller_location
      : stockItemRequest?.seller_location;
  }, [
    stockItemRequest?.original_seller_location,
    stockItemRequest?.seller_location,
  ]);

  const sellerCompanyEmail = useMemo(
    () =>
      requestType === RequestTypes.Quote
        ? stockItemRequest?.quote_details?.recipient_email
        : stockItemRequest?.seller_location?.contact_email
        ? stockItemRequest?.seller_location?.contact_email.replaceAll(";", "; ")
        : "",
    [stockItemRequest?.seller_location?.contact_email]
  );
  const originalSellerCompanyEmail = useMemo(
    () =>
      originalSellerLocation?.contact_email
        ? originalSellerLocation?.contact_email.replaceAll(";", "; ")
        : "",
    [originalSellerLocation?.contact_email]
  );
  const buyerCompanyEmail = useMemo(
    () =>
      requestType === RequestTypes.Quote
        ? stockItemRequest?.quote_details?.recipient_email
        : stockItemRequest?.buyer_location
        ? (!isCustomApp || requestType === RequestTypes.Order
            ? stockItemRequest?.buyer_location?.contact_email
              ? stockItemRequest?.buyer_location?.contact_email
              : "-"
            : stockItemRequest?.buyer_location?.buyer?.email
          )?.replaceAll(";", "; ")
        : "",
    [stockItemRequest.buyer_location]
  );

  const shareableLinkView = () => (
    <ShareableLinkRow>
      <ShareableLinkContainer>
        <PressableContainer
          onPress={async () => {
            await Clipboard.setStringAsync(
              stockItemRequest.shareable_link.link
            );
            showToast(
              t("requestHistoryDetail.success.text", "Success"),
              t("requestHistoryDetail.copyURL.toastMessage.text"),
              ToastTypes.SUCCESS,
              true,
              true
            );
          }}
        >
          <VDBIcon
            color={theme["main-01"]}
            size={16}
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.COPY}
          />
          <VDBText type={typography["Minor-M"]} color={theme["link-static"]}>
            {t("requestHistoryDetail.copyURL.text")}
          </VDBText>
        </PressableContainer>
        <HorizontalSeparator />
      </ShareableLinkContainer>
      <PressableContainer
        onPress={async () => {
          const supported = await Linking.canOpenURL(
            stockItemRequest.shareable_link.link
          );
          if (supported) {
            Linking.openURL(stockItemRequest.shareable_link.link);
          }
        }}
      >
        <VDBIcon
          color={theme["main-01"]}
          size={16}
          variant={IconsVariant.VDBCustom}
          name={VDBIcons.EXTERNAL_LINK}
        />
        <VDBText type={typography["Minor-M"]} color={theme["link-static"]}>
          {t("requestHistoryDetail.openInNewTab.text")}
        </VDBText>
      </PressableContainer>
    </ShareableLinkRow>
  );

  return (
    <CardContainer>
      <CollapsibleView
        initExpanded={true}
        applyTopPadding={false}
        applyShadow
        arrowStyling={{ color: theme["core-04"] }}
        title={
          stockItemRequest.view_type === "buyer_view" ? (
            (
              requestType === RequestTypes.Quote
                ? recipientEmailAvailable
                : requestType !== RequestTypes.Order
            ) ? (
              <StyledView>
                {requestType === RequestTypes.Quote && (
                  <VDBText
                    type={typography["Minor-R"]}
                    color={theme["core-06"]}
                  >
                    {t("requestHistoryDetail.receiverdetails.text")}
                  </VDBText>
                )}
                <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
                  {requestType === RequestTypes.Quote
                    ? stockItemRequest?.quote_details?.recipient_company_name
                    : stockItemRequest?.seller_location?.company_name}
                </VDBText>
                <VDBText
                  type={
                    theme.isLargeScreen
                      ? typography["Body-R"]
                      : typography["Minor-R"]
                  }
                  color={theme["core-06"]}
                >
                  {requestType === RequestTypes.Quote
                    ? stockItemRequest?.quote_details
                        ?.recipient_company_location
                    : `${stockItemRequest?.seller_location?.city}, ${stockItemRequest?.seller_location?.country}`}
                </VDBText>
              </StyledView>
            ) : (
              <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
                {requestType === RequestTypes.Order
                  ? t("requestHistoryDetail.suppliersdetails.text")
                  : t("requestHistoryDetail.receiverdetails.text")}
              </VDBText>
            )
          ) : showRequestItemFromSupplier(stockItemRequest) ? (
            <StyledView>
              <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
                {t("requestHistoryDetail.itemBelongsTo.text")}
              </VDBText>
              <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
                {originalSellerLocation?.company_name}
              </VDBText>
              <VDBText
                type={
                  theme.isLargeScreen
                    ? typography["Body-R"]
                    : typography["Minor-R"]
                }
                color={theme["core-06"]}
              >
                {originalSellerLocation?.state},{" "}
                {originalSellerLocation?.country}
              </VDBText>
            </StyledView>
          ) : (
            <StyledView>
              <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
                {requestType === RequestTypes.Quote
                  ? t("requestHistoryDetail.suppliersdetails.text")
                  : t("requestHistoryDetail.buyerDetails.text")}
              </VDBText>
              <VDBText type={typography["H3-M"]} color={theme["core-05"]}>
                {stockItemRequest?.buyer_location?.company_name}
              </VDBText>
              <VDBText
                type={
                  theme.isLargeScreen
                    ? typography["Body-R"]
                    : typography["Minor-R"]
                }
                color={theme["core-06"]}
              >
                {stockItemRequest?.buyer_location?.city},{" "}
                {stockItemRequest?.buyer_location?.country}
              </VDBText>
            </StyledView>
          )
        }
      >
        <LineViewContainer background={"core-02"} />
        <CompanyDetailBottomContainer>
          {stockItemRequest.view_type === "buyer_view" ? (
            <>
              <LabelViewContainer>
                <LabelView
                  itemLeftText={t("requestHistoryDetail.contact.text")}
                  itemRightText={
                    requestType === RequestTypes.Quote
                      ? stockItemRequest?.quote_details?.recipient_name
                      : stockItemRequest?.seller_location?.contact_person
                  }
                  keyTypographyStyle={
                    theme.isLargeScreen
                      ? typography["Body-R"]
                      : typography["Minor-R"]
                  }
                  valueTypographyStyle={
                    theme.isLargeScreen
                      ? typography["Body-M"]
                      : typography["Minor-M"]
                  }
                  leftPostfixText=""
                  isFixedKeyWidth={true}
                  keyWidthPercent={keyWidthPercent}
                  ellipsize={false}
                />
              </LabelViewContainer>
              {(getRequestType(stockItemRequest.request_type) ===
              RequestTypes.Quote
                ? isEmailVisible
                : true) && (
                <LabelViewContainer>
                  <LabelView
                    itemLeftText={t("requestHistoryDetail.email.text")}
                    itemRightText={sellerCompanyEmail}
                    keyTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-R"]
                        : typography["Minor-R"]
                    }
                    valueTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-M"]
                        : typography["Minor-M"]
                    }
                    leftPostfixText=""
                    isFixedKeyWidth={true}
                    keyWidth={50}
                    keyWidthPercent={keyWidthPercent}
                    ellipsize={false}
                    isFixWidth
                    isFromMatchPair
                    emailClickable={true}
                    // rightPrefixTextTheme={theme["link-static"]}
                  />
                </LabelViewContainer>
              )}

              {stockItemRequest?.seller_location?.office_phone &&
                getRequestType(stockItemRequest?.request_type) !==
                  RequestTypes.Quote && (
                  <LabelViewContainer>
                    <LabelView
                      itemLeftText={t("requestHistoryDetail.office.text")}
                      itemRightText={
                        stockItemRequest?.seller_location?.office_phone
                      }
                      keyTypographyStyle={
                        theme.isLargeScreen
                          ? typography["Body-R"]
                          : typography["Minor-R"]
                      }
                      valueTypographyStyle={
                        theme.isLargeScreen
                          ? typography["Body-M"]
                          : typography["Minor-M"]
                      }
                      countryCode={
                        stockItemRequest?.seller_location
                          ?.contact_phone_country_code
                      }
                      leftPostfixText=""
                      isFixedKeyWidth={true}
                      keyWidthPercent={keyWidthPercent}
                      ellipsize={false}
                      marginRightValue={80}
                      clickableHandle={true}
                    />
                  </LabelViewContainer>
                )}
              {stockItemRequest?.seller_location?.mobile_phone &&
                (getRequestType(stockItemRequest?.request_type) ===
                RequestTypes.Quote
                  ? isPhoneNumberVisible
                  : true) && (
                  <LabelViewContainer>
                    <LabelView
                      itemLeftText={t("requestHistoryDetail.mobile.text")}
                      itemRightText={
                        getRequestType(stockItemRequest?.request_type) ===
                        RequestTypes.Quote
                          ? stockItemRequest?.quote_details
                              ?.recipient_phone_number
                          : stockItemRequest?.seller_location?.mobile_phone
                      }
                      countryCode={
                        getRequestType(stockItemRequest?.request_type) ===
                        RequestTypes.Quote
                          ? stockItemRequest?.quote_details
                              ?.recipient_phone_country_code
                          : stockItemRequest?.seller_location
                              ?.mobile_phone_country_code
                      }
                      keyTypographyStyle={
                        theme.isLargeScreen
                          ? typography["Body-R"]
                          : typography["Minor-R"]
                      }
                      valueTypographyStyle={
                        theme.isLargeScreen
                          ? typography["Body-M"]
                          : typography["Minor-M"]
                      }
                      leftPostfixText=""
                      isFixedKeyWidth={true}
                      keyWidthPercent={keyWidthPercent}
                      ellipsize={false}
                      clickableHandle={true}
                    />
                  </LabelViewContainer>
                )}
            </>
          ) : showRequestItemFromSupplier(stockItemRequest) ? (
            <>
              <LabelViewContainer>
                <LabelView
                  itemLeftText={t("requestHistoryDetail.contact.text")}
                  itemRightText={originalSellerLocation?.contact_person}
                  keyTypographyStyle={
                    theme.isLargeScreen
                      ? typography["Body-R"]
                      : typography["Minor-R"]
                  }
                  valueTypographyStyle={
                    theme.isLargeScreen
                      ? typography["Body-M"]
                      : typography["Minor-M"]
                  }
                  leftPostfixText=""
                  isFixedKeyWidth={true}
                  keyWidthPercent={keyWidthPercent}
                  ellipsize={false}
                />
              </LabelViewContainer>
              {
                <LabelViewContainer>
                  <LabelView
                    itemLeftText={t("requestHistoryDetail.email.text")}
                    itemRightText={originalSellerCompanyEmail}
                    keyTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-R"]
                        : typography["Minor-R"]
                    }
                    valueTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-M"]
                        : typography["Minor-M"]
                    }
                    leftPostfixText=""
                    isFixedKeyWidth={true}
                    keyWidth={50}
                    keyWidthPercent={keyWidthPercent}
                    ellipsize={false}
                    isFixWidth
                    isFromMatchPair
                    emailClickable={true}
                  />
                </LabelViewContainer>
              }

              {originalSellerLocation.office_phone && (
                <LabelViewContainer>
                  <LabelView
                    itemLeftText={t("requestHistoryDetail.office.text")}
                    itemRightText={originalSellerLocation.office_phone}
                    countryCode={
                      originalSellerLocation?.contact_phone_country_code
                    }
                    keyTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-R"]
                        : typography["Minor-R"]
                    }
                    valueTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-M"]
                        : typography["Minor-M"]
                    }
                    leftPostfixText=""
                    isFixedKeyWidth={true}
                    keyWidthPercent={keyWidthPercent}
                    ellipsize={false}
                    marginRightValue={80}
                    clickableHandle={true}
                  />
                </LabelViewContainer>
              )}
              {originalSellerLocation.mobile_phone && (
                <LabelViewContainer>
                  <LabelView
                    itemLeftText={t("requestHistoryDetail.mobile.text")}
                    itemRightText={originalSellerLocation.mobile_phone}
                    countryCode={
                      originalSellerLocation?.mobile_phone_country_code
                    }
                    keyTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-R"]
                        : typography["Minor-R"]
                    }
                    valueTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-M"]
                        : typography["Minor-M"]
                    }
                    leftPostfixText=""
                    isFixedKeyWidth={true}
                    keyWidthPercent={keyWidthPercent}
                    ellipsize={false}
                    clickableHandle={true}
                  />
                </LabelViewContainer>
              )}
            </>
          ) : (
            <>
              <LabelViewContainer>
                <LabelView
                  itemLeftText={t("requestHistoryDetail.contact.text")}
                  itemRightText={
                    !isCustomApp || requestType === RequestTypes.Order
                      ? stockItemRequest?.buyer_location?.contact_person
                      : stockItemRequest?.buyer_location.buyer?.name
                  }
                  keyTypographyStyle={
                    theme.isLargeScreen
                      ? typography["Body-R"]
                      : typography["Minor-R"]
                  }
                  valueTypographyStyle={
                    theme.isLargeScreen
                      ? typography["Body-M"]
                      : typography["Minor-M"]
                  }
                  leftPostfixText=""
                  isFixedKeyWidth={true}
                  keyWidthPercent={keyWidthPercent}
                  ellipsize={false}
                />
              </LabelViewContainer>
              {(getRequestType(stockItemRequest?.request_type) ===
              RequestTypes.Quote
                ? isEmailVisible
                : true) && (
                <LabelViewContainer>
                  <LabelView
                    itemLeftText={t("requestHistoryDetail.email.text")}
                    itemRightText={buyerCompanyEmail}
                    keyTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-R"]
                        : typography["Minor-R"]
                    }
                    valueTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-M"]
                        : typography["Minor-M"]
                    }
                    leftPostfixText=""
                    isFixedKeyWidth={true}
                    keyWidth={50}
                    keyWidthPercent={keyWidthPercent}
                    ellipsize={false}
                    isFixWidth
                    isFromMatchPair
                    emailClickable={true}
                    // rightPrefixTextTheme={theme["link-static"]}
                  />
                </LabelViewContainer>
              )}
              {(!isCustomApp || requestType === RequestTypes.Order
                ? stockItemRequest?.buyer_location?.office_phone
                : stockItemRequest?.buyer_location.buyer?.phone_number) &&
                (getRequestType(stockItemRequest?.request_type) ===
                RequestTypes.Quote
                  ? isPhoneNumberVisible
                  : true) && (
                  <LabelViewContainer>
                    <LabelView
                      itemLeftText={t("requestHistoryDetail.office.text")}
                      itemRightText={
                        !isCustomApp || requestType === RequestTypes.Order
                          ? stockItemRequest?.buyer_location?.office_phone
                          : stockItemRequest?.buyer_location.buyer?.phone_number
                      }
                      countryCode={
                        !isCustomApp || requestType === RequestTypes.Order
                          ? stockItemRequest?.buyer_location
                              ?.office_phone_country_code
                          : stockItemRequest?.buyer_location?.buyer
                              ?.phone_number_country_code
                      }
                      keyTypographyStyle={
                        theme.isLargeScreen
                          ? typography["Body-R"]
                          : typography["Minor-R"]
                      }
                      valueTypographyStyle={
                        theme.isLargeScreen
                          ? typography["Body-M"]
                          : typography["Minor-M"]
                      }
                      leftPostfixText=""
                      isFixedKeyWidth={true}
                      keyWidthPercent={keyWidthPercent}
                      ellipsize={false}
                      clickableHandle={true}
                    />
                  </LabelViewContainer>
                )}
              {(!isCustomApp || requestType === RequestTypes.Order
                ? stockItemRequest?.buyer_location?.mobile_phone
                : stockItemRequest?.buyer_location?.buyer?.mobile_phone) && (
                <LabelViewContainer>
                  <LabelView
                    itemLeftText={t("requestHistoryDetail.mobile.text")}
                    itemRightText={
                      !isCustomApp || requestType === RequestTypes.Order
                        ? stockItemRequest?.buyer_location?.mobile_phone
                        : stockItemRequest?.buyer_location?.buyer?.mobile_phone
                    }
                    countryCode={
                      !isCustomApp || requestType === RequestTypes.Order
                        ? stockItemRequest?.buyer_location
                            ?.mobile_phone_country_code
                        : stockItemRequest?.buyer_location.buyer
                            ?.mobile_phone_country_code
                    }
                    keyTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-R"]
                        : typography["Minor-R"]
                    }
                    valueTypographyStyle={
                      theme.isLargeScreen
                        ? typography["Body-M"]
                        : typography["Minor-M"]
                    }
                    leftPostfixText=""
                    isFixedKeyWidth={true}
                    keyWidthPercent={keyWidthPercent}
                    ellipsize={false}
                    clickableHandle={true}
                  />
                </LabelViewContainer>
              )}
            </>
          )}
          {getRequestType(stockItemRequest?.request_type) ===
            RequestTypes.Quote && (
            <LabelViewContainer>
              <LabelView
                itemLeftText={t("requestHistoryDetail.shareableLink.text")}
                itemRightView={shareableLinkView()}
                keyTypographyStyle={
                  theme.isLargeScreen
                    ? typography["Body-R"]
                    : typography["Minor-R"]
                }
                valueTypographyStyle={
                  theme.isLargeScreen
                    ? typography["Body-M"]
                    : typography["Minor-M"]
                }
                leftPostfixText=""
                isFixedKeyWidth={true}
                keyWidthPercent={keyWidthPercent}
                ellipsize={false}
              />
            </LabelViewContainer>
          )}
        </CompanyDetailBottomContainer>
        {!isJbtDisabled && stockItemRequest?.vendor_info?.jbt_detail && (
          <>
            <LineViewContainer background={"core-02"} />
            <Spacer size={SpaceType.Basic} />
            <JBTDetail
              logoUrl={stockItemRequest?.vendor_info?.jbt_detail?.icon_url}
              member_type={
                stockItemRequest?.vendor_info?.jbt_detail?.member_type
              }
              info_text={stockItemRequest?.vendor_info?.jbt_detail?.info_text}
              vendor_name={stockItemRequest?.vendor_info?.company_name}
              mItem={stockItemRequest?.diamond}
              jbtDetail={stockItemRequest?.vendor_info?.jbt_detail}
              screenName={"notification_page"}
            />
          </>
        )}
      </CollapsibleView>
    </CardContainer>
  );
};
const CardContainer = styled.View`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme["core-03"]};
`;
const CompanyDetailBottomContainer = styled.View`
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
`;
const LabelViewContainer = styled.View`
  padding: ${SpaceType.ExtraSmall}px ${SpaceType.Double}px;
  overflow: hidden;
`;

const ShareableLinkContainer = styled.View`
  flex-direction: row;
  gap: ${SpaceType.Basic}px;
  align-items: center;
`;

const ShareableLinkRow = styled.View`
  flex-direction: ${({ theme }) => (theme.isMobile ? "column" : "row")};
  gap: ${SpaceType.Basic}px;
`;

const PressableContainer = styled.TouchableOpacity`
  flex-direction: row;
  gap: ${SpaceType.Basic}px;
  align-items: center;
`;

const HorizontalSeparator = styled.View`
  height: 16px;
  width: 1px;
  background-color: ${({ theme }) => theme["core-05"]};
`;

const StyledView = styled.View`
  gap: ${SpaceType.ExtraSmall}px;
`;

export default CompanyDetailsCard;
